import { Component } from "react";
import {
    clearAuthStorage,
    clearVolatileAppState,
    consumeRecoveryReloadFlag,
    hasRecoveryReloadFlag,
    markRecoveryReload,
} from "@/lib/clientStorage";

/**
 * Top-level recovery boundary: clears volatile corrupted state and reloads once.
 * Never leaves the user on an infinite "Recovering session" spinner.
 */
export class AppRecoveryBoundary extends Component {
    constructor(props) {
        super(props);
        this.state = { failed: false, fatal: false };
        this.recoveryTimer = null;
    }

    static getDerivedStateFromError() {
        return { failed: true };
    }

    componentDidCatch(error, errorInfo) {
        console.error("[FairShare] Recovering from render error:", error, errorInfo);
    }

    componentDidUpdate(_prevProps, prevState) {
        if (!this.state.failed || prevState.failed) return;
        this.scheduleRecovery();
    }

    componentWillUnmount() {
        if (this.recoveryTimer) {
            window.clearTimeout(this.recoveryTimer);
            this.recoveryTimer = null;
        }
    }

    scheduleRecovery() {
        if (this.recoveryTimer) return;

        if (hasRecoveryReloadFlag()) {
            clearAuthStorage();
            this.setState({ fatal: true, failed: false });
            return;
        }

        clearVolatileAppState();
        markRecoveryReload();

        this.recoveryTimer = window.setTimeout(() => {
            this.recoveryTimer = null;
            window.location.reload();
        }, 250);

        // Escape hatch if reload does not happen (blocked navigation, etc.)
        window.setTimeout(() => {
            if (this.state.failed && !this.state.fatal) {
                clearAuthStorage();
                this.setState({ fatal: true, failed: false });
            }
        }, 4000);
    }

    render() {
        if (this.state.fatal) {
            return (
                <div className="grid min-h-screen place-items-center bg-[#1E1E1E] px-6 text-center text-white">
                    <div className="max-w-md space-y-3">
                        <p className="font-heading text-xl">Something went wrong</p>
                        <p className="text-sm text-white/55">
                            We cleared incompatible cached data. Please sign in again to continue.
                        </p>
                        <button
                            type="button"
                            className="btn-neon rounded-md px-4 py-2 text-sm"
                            onClick={() => {
                                consumeRecoveryReloadFlag();
                                clearAuthStorage();
                                window.location.href = "/login";
                            }}
                        >
                            Go to login
                        </button>
                    </div>
                </div>
            );
        }

        if (this.state.failed) {
            return (
                <div className="grid min-h-screen place-items-center bg-[#1E1E1E] px-6 text-center text-white">
                    <div className="max-w-md space-y-4">
                        <p className="text-sm text-white/60">Recovering session…</p>
                        <button
                            type="button"
                            className="btn-ghost rounded-md px-4 py-2 text-sm text-white/70"
                            onClick={() => {
                                consumeRecoveryReloadFlag();
                                clearAuthStorage();
                                window.location.href = "/login";
                            }}
                        >
                            Sign in again
                        </button>
                    </div>
                </div>
            );
        }

        return this.props.children;
    }
}
